import instanceFactory from '@InstanceFactory';
import { ComponentType, EnumValue } from '@Common/Enums';
import { DriverDisplayMode, SingleSelectionMenuOrientation } from '@Common/Enums/Enum';
import { componentDescriptors } from '@View/Core/ComponentDescriptors/ComponentDescriptors';

export interface IComponentDescriptorHelper {
    getComponentDescriptor(componentType);

    getComponentDescriptorProperty<T>(componentType: string, readProperty: (desc: any) => T, defaultValue?: T): T;

    getComponentDescriptorProperty<T>(componentType: string, readPropertyPath: string, defaultValue?: T): T;

    getDimensionDescriptors(componentType): { id: string; title: string }[];

    getToolbarComponentDescriptors(componentGroup);

    createComponent(componentType);

    createAppearance(componentType);

    isHorisontalMenuStyledDriver(appearance);

    getComponentChangeTypes(componentType: string): EnumValue<ComponentType>[];
}

export interface IComponentDescriptor {
    componentType: string;
    componentGroup: string;
    componentInstanceType: string;
    toolBarIcon?: string;
    changableGroup?: string;
    componentDataDescriptor?: {
        dimensionDescriptors: any[];
    };
}

class ComponentDescriptorHelper implements IComponentDescriptorHelper {
    componentDescriptors: { [key: string]: IComponentDescriptor };
    componentAppearances: { [key: string]: any };

    constructor() {
        this.componentDescriptors = {};
        Object.keys(componentDescriptors)
            .filter(descriptorTypeName => _.isFunction(componentDescriptors[descriptorTypeName]))
            .forEach(descriptorTypeName => {
                const descriptor = componentDescriptors[descriptorTypeName]();
                if (!descriptor.componentType) {
                    throw 'Undefined componentType for descriptor ' + descriptorTypeName;
                }
                this.componentDescriptors[descriptor.componentType] = descriptor;
            });

        this.componentAppearances = Object.getOwnPropertyNames(instanceFactory)
            .map(typeName => instanceFactory[typeName])
            .filter(typeInst => typeInst.appearanceComponentType !== undefined)
            .searchTree(typeInst => typeInst.appearanceComponentType);
    }

    getComponentDescriptor(componentType) {
        const descriptor = this.componentDescriptors[componentType];
        if (descriptor !== undefined) {
            return descriptor;
        } else {
            throw 'Cannot find descriptor for ' + componentType;
        }
    }

    getComponentDescriptorProperty(componentType, readProperty, defaultValue) {
        const readPropertyImpl = typeof readProperty === 'string' ? desc => _.get(desc, readProperty) : readProperty;
        const descriptor = this.componentDescriptors[componentType];
        const property = descriptor && readPropertyImpl(descriptor);
        if (typeof descriptor === 'undefined' || typeof property === 'undefined') {
            return defaultValue;
        }
        return property;
    }

    getDimensionDescriptors(componentType) {
        const componentDescriptor = this.getComponentDescriptor(componentType);
        return componentDescriptor && componentDescriptor.componentDataDescriptor
            ? componentDescriptor.componentDataDescriptor.dimensionDescriptors
            : [];
    }

    getToolbarComponentDescriptors(componentGroup) {
        const descriptors = toolbarComponentTypes
            .map(toolBarType => this.getComponentDescriptor(toolBarType))
            .filter(desc => desc.componentGroup === componentGroup);
        return descriptors;
    }

    createAppearance(componentType) {
        const appearanceType = this.componentAppearances[componentType];
        return appearanceType.create();
    }

    createComponent(componentType) {
        const descriptor = this.getComponentDescriptor(componentType);
        return instanceFactory[descriptor.componentInstanceType].create();
    }

    isHorisontalMenuStyledDriver(appearance) {
        return (
            (appearance.isSimpleMode || appearance.ComponentType === ComponentType.entityDriver) &&
            appearance.displayMode === DriverDisplayMode.menuStyle &&
            appearance.simpleModeSettings.menuOrientation === SingleSelectionMenuOrientation.horizontal
        );
    }

    getComponentChangeTypes(componentType: string): EnumValue<ComponentType>[] {
        return this.findChangeTypesId(componentType).map(componentTypeId => ComponentType.getValue(componentTypeId));
    }

    private findChangeTypesId(componentType: string): string[] {
        const descriptor = this.componentDescriptors[componentType];
        if (!descriptor.changableGroup) {
            return [componentType];
        }
        return Object.getOwnPropertyNames(this.componentDescriptors)
            .filter(descType => this.componentDescriptors[descType].changableGroup === descriptor.changableGroup)
            .map(descType => this.componentDescriptors[descType].componentType);
    }
}

export const componentDescriptorHelper: IComponentDescriptorHelper = new ComponentDescriptorHelper();

const toolbarComponentTypes = [
    ComponentType.bar,
    ComponentType.line,
    ComponentType.stackedBar,
    ComponentType.chartWithRange,
    ComponentType.area,
    ComponentType.areaRange,
    ComponentType.horizontalBar,
    ComponentType.bubble,
    ComponentType.scatter,
    ComponentType.sparkLine,
    ComponentType.trafficLight,
    ComponentType.dialGauge,
    ComponentType.linearGauge,
    // ComponentType.recordTime,
    ComponentType.candleStick,
    ComponentType.pareto,
    ComponentType.waterfall,
    ComponentType.pie,
    ComponentType.treeMap,
    ComponentType.map,
    ComponentType.processControl,
    ComponentType.metricGrid,
    ComponentType.recordGrid,
    ComponentType.alertsGrid,
    ComponentType.alertMessagesGrid,
    ComponentType.markerGrid,
    ComponentType.driver,
    ComponentType.timeDriver,
    ComponentType.periodDriver,
    ComponentType.aggregationDriver,
    ComponentType.timeWindow,
    ComponentType.globalFilters,
    ComponentType.entityDriver,
    ComponentType.parameterDriver,
    ComponentType.label,
    ComponentType.image,
    ComponentType.uiLine,
    ComponentType.button,
    ComponentType.viewHolder,
    ComponentType.menu,
    ComponentType.searchPanel,
    ComponentType.reportSelfSubscription,
    ComponentType.recordForm,
    ComponentType.swimlane,
    ComponentType.processChart,
    ComponentType.processStepChart,
    ComponentType.processGrid,
    ComponentType.languageSelection,
    ComponentType.trellisChart,
    ComponentType.processTrellisChart,
    ComponentType.polarChartColumn,
    ComponentType.pyramid,
    ComponentType.pieSemiCircle,
    ComponentType.funnel,
    ComponentType.boxPlot,
    ComponentType.fileUploader
];
